import React, { useState } from 'react';
import { Spin, Popover } from 'antd';
import { TagOutlined } from '@ant-design/icons';
import { connect } from 'react-redux';
import LabelChip from './LabelChip';
import { WorkOrderLabel } from '../../data/workOrderStarLabels';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import { PERMISSION_NAMES } from '../../utils/AuthUtils';
import { canAddWOLabelsOrStarNotes } from '../../utils/DataAccessUtils';
import useExclusivePopover from '../../hooks/useExclusivePopover';
import ProUpsellContent from './ProUpsellContent';
import AddLabelChip, { ADD_LABEL_CHIP_REVEAL_ON_HOVER_CLASS } from './AddLabelChip';

interface Props {
	labels: WorkOrderLabel[];
	maxVisible?: number;
	showAddButton?: boolean;
	/** Figma detail header: hollow pill with white outline on priority-colored background */
	addButtonVariant?: 'default' | 'hollowOnDark';
	/** Row usage: add chip stays hidden until the row (nearest `group` ancestor) is hovered, and fills grey/2 on its own hover. */
	revealAddButtonOnHover?: boolean;
	onAddClick?: (event: React.MouseEvent) => void;
	onChipClick?: (label: WorkOrderLabel) => void;
	onOverflowClick?: (event: React.MouseEvent) => void;
	className?: string;
	loading?: boolean;
	/** `inline` hoists chips into the parent text flow (detail header title row). */
	layout?: 'row' | 'inline';
	/**
	 * Pro entitlement (injected via connect). When false, clicking the add ("+")
	 * chip shows the upgrade-to-Pro upsell instead of opening the assign flow.
	 */
	canUseLabels?: boolean;
}

const SUMMARY_CHIP_BASE_CLASS =
	'footnoteregular box-border h-[30px] cursor-pointer items-center gap-1 rounded-full border border-solid !border-grey-5 bg-white px-2 py-1 text-grey-10';
const SUMMARY_CHIP_CLASS = `inline-flex ${SUMMARY_CHIP_BASE_CLASS}`;

/** Figma: 12×12 tag icon in summary / add-label pills */
const SUMMARY_TAG_ICON_CLASS =
	'inline-flex h-3 w-3 shrink-0 items-center justify-center text-[12px] leading-none';

const WorkOrderLabelChipsRow: React.FC<Props> = ({
	labels,
	maxVisible = 2,
	showAddButton = false,
	addButtonVariant = 'default',
	revealAddButtonOnHover = false,
	onAddClick,
	onChipClick,
	onOverflowClick,
	className,
	loading = false,
	layout = 'row',
	canUseLabels = true,
}) => {
	const [upsellOpen, setUpsellOpen] = useState(false);
	// Only one floating popover (this upsell, the star-note popover, …) open at a time.
	useExclusivePopover(upsellOpen, () => setUpsellOpen(false));
	if (!labels.length && !showAddButton) return null;
	const visible = labels.slice(0, maxVisible);
	const overflow = labels.length - visible.length;
	const isInline = layout === 'inline';

	const handleSummaryClick = (event: React.MouseEvent) => {
		event.stopPropagation();
		event.preventDefault();
		if (onAddClick) onAddClick(event);
	};

	// Add ("+") chip click: non-Pro users get the upgrade upsell instead of the
	// assign-label flow. The button itself stays visible (gated only on click) so
	// the upsell is discoverable, matching the star-with-note Pro gate.
	const handleAddClick = (event: React.MouseEvent) => {
		event.stopPropagation();
		event.preventDefault();
		if (!canUseLabels) {
			setUpsellOpen(true);
			return;
		}
		if (onAddClick) onAddClick(event);
	};

	const loadingClass = loading ? 'pointer-events-none opacity-50' : '';

	const renderOverflowChip = (onClick: (event: React.MouseEvent) => void) => (
		<span
			role="button"
			tabIndex={0}
			onClick={onClick}
			className={`${SUMMARY_CHIP_CLASS}${loadingClass ? ` ${loadingClass}` : ''}`}
		>
			<span>{`+ ${overflow}`}</span>
			<TagOutlined size={12} className={SUMMARY_TAG_ICON_CLASS} />
		</span>
	);

	const chips = (
		<>
			{visible.map((label) => (
				<LabelChip
					key={label.id}
					label={label}
					className={loadingClass || undefined}
					onClick={onChipClick ? () => onChipClick(label) : undefined}
				/>
			))}
			{overflow > 0 ? (
				onOverflowClick ? (
					<AccessPermissionChecker
						name={PERMISSION_NAMES.APPLY_WORK_ORDER_LABELS}
						permissionDeniedContent={renderOverflowChip(onOverflowClick)}
					>
						{renderOverflowChip(handleSummaryClick)}
					</AccessPermissionChecker>
				) : (
					renderOverflowChip(handleSummaryClick)
				)
			) : showAddButton ? (
				<AccessPermissionChecker name={PERMISSION_NAMES.APPLY_WORK_ORDER_LABELS}>
					<Popover
						content={
							<ProUpsellContent
								message="Pro users can add labels to work orders"
								onClose={() => setUpsellOpen(false)}
							/>
						}
						trigger={[]}
						visible={upsellOpen}
						onVisibleChange={setUpsellOpen}
						overlayClassName="!w-[400px]"
						placement="bottomLeft"
					>
						<AddLabelChip
							onClick={handleAddClick}
							variant={addButtonVariant === 'hollowOnDark' ? 'hollowOnDark' : 'default'}
							displayClassName={
								revealAddButtonOnHover ? ADD_LABEL_CHIP_REVEAL_ON_HOVER_CLASS : 'inline-flex'
							}
							disabled={loading}
						/>
					</Popover>
				</AccessPermissionChecker>
			) : null}
		</>
	);

	// Inline layout: render chips as direct flex children (no Spin/div wrapper — ant-spin-container
	// is block-level and would force chips onto their own row below the title).
	const rowContent = isInline ? (
		chips
	) : (
		<Spin spinning={loading}>
			<div
				className={`inline-flex flex-wrap items-center gap-3${className ? ` ${className}` : ''}`}
			>
				{chips}
			</div>
		</Spin>
	);

	// Outer gate: no labels feature at all without VIEW. APPLY (above, on the overflow/add
	// chip) stays the inner gate — you can be granted View without Apply for read-only access.
	return (
		<AccessPermissionChecker name={PERMISSION_NAMES.VIEW_WORK_ORDER_LABELS}>
			{rowContent}
		</AccessPermissionChecker>
	);
};

const mapStateToProps = (state) => ({
	canUseLabels: canAddWOLabelsOrStarNotes(state.session.currentUser),
});

export default connect(mapStateToProps)(WorkOrderLabelChipsRow);
